import type { DraftStep } from "@zworks/console-contracts";

export interface NodePosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PortPosition {
  x: number;
  y: number;
}

export interface NodeLayout {
  step: DraftStep;
  position: NodePosition;
  inputPort: PortPosition;
  outputPort: PortPosition;
  approvePort?: PortPosition;
  rejectPort?: PortPosition;
}

export const NODE_WIDTH = 300;
export const NODE_HEIGHT = 160;
export const HORIZONTAL_GAP = 90;
export const VERTICAL_GAP = 70;
export const CANVAS_PADDING = 80;
/** Radius of the connection port dots, used to stop edges at the dot perimeter. */
export const PORT_RADIUS = 8;

/**
 * Calculates responsive DAG layout positions for flow steps.
 * When steps have `dependsOn`, steps are grouped into topological ranks
 * so that parallel tasks naturally stack vertically at the same column.
 * If custom positions exist, they take precedence.
 */
export function calculateFlowLayout(
  steps: DraftStep[],
  customPositions?: Record<string, { x: number; y: number }>,
): Map<string, NodeLayout> {
  const layouts = new Map<string, NodeLayout>();
  const stepMap = new Map<string, DraftStep>();
  steps.forEach((s) => stepMap.set(s.id, s));

  // Determine if any step has explicit dependsOn defined
  const hasExplicitDependencies = steps.some((s) => s.dependsOn && s.dependsOn.length > 0);

  // Compute rank for each step if explicit dependencies exist
  const ranks = new Map<string, number>();
  if (hasExplicitDependencies) {
    const getRank = (stepId: string, visited = new Set<string>()): number => {
      if (ranks.has(stepId)) return ranks.get(stepId)!;
      if (visited.has(stepId)) return 0;
      visited.add(stepId);

      const targetStep = stepMap.get(stepId);
      if (!targetStep || !targetStep.dependsOn || targetStep.dependsOn.length === 0) {
        ranks.set(stepId, 0);
        return 0;
      }

      let maxParentRank = -1;
      for (const parentId of targetStep.dependsOn) {
        maxParentRank = Math.max(maxParentRank, getRank(parentId, visited));
      }

      const currentRank = maxParentRank + 1;
      ranks.set(stepId, currentRank);
      return currentRank;
    };

    steps.forEach((s) => getRank(s.id));
  }

  // Count items per rank to space them vertically
  const rankCounts = new Map<number, number>();

  steps.forEach((step, index) => {
    const custom = customPositions?.[step.id];

    let defaultX: number;
    let defaultY: number;

    if (hasExplicitDependencies) {
      const rank = ranks.get(step.id) ?? 0;
      const rankIndex = rankCounts.get(rank) ?? 0;
      rankCounts.set(rank, rankIndex + 1);

      defaultX = CANVAS_PADDING + rank * (NODE_WIDTH + HORIZONTAL_GAP);
      defaultY = CANVAS_PADDING + 80 + rankIndex * (NODE_HEIGHT + VERTICAL_GAP);
    } else {
      defaultX = CANVAS_PADDING + index * (NODE_WIDTH + HORIZONTAL_GAP);
      defaultY = CANVAS_PADDING + 80;
    }

    const x = custom?.x ?? defaultX;
    const y = custom?.y ?? defaultY;

    const nodePos: NodePosition = {
      x,
      y,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    };

    const inputPort: PortPosition = {
      x: x + NODE_WIDTH / 2,
      y,
    };

    const outputPort: PortPosition = {
      x: x + NODE_WIDTH / 2,
      y: y + NODE_HEIGHT,
    };

    const approvePort: PortPosition | undefined = step.gate
      ? {
          x: x + NODE_WIDTH / 2 - 24,
          y: y + NODE_HEIGHT,
        }
      : undefined;

    const rejectPort: PortPosition | undefined = step.gate
      ? {
          x: x + NODE_WIDTH / 2 + 24,
          y: y + NODE_HEIGHT,
        }
      : undefined;

    layouts.set(step.id, {
      step,
      position: nodePos,
      inputPort,
      outputPort,
      approvePort,
      rejectPort,
    });
  });

  return layouts;
}

/**
 * Generates an SVG cubic Bezier curve string between two points.
 */
export function createBezierCurve(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): string {
  const dy = Math.max(40, Math.abs(endY - startY) * 0.5);
  return `M ${startX} ${startY} C ${startX} ${startY + dy}, ${endX} ${endY - dy}, ${endX} ${endY}`;
}

/**
 * Generates an SVG backwards loop curve for Gate reject routes returning to an earlier step.
 */
export function createRejectRouteCurve(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): string {
  const dropY = Math.max(startY, endY) + 85;
  const loopOffset = 50;
  return `M ${startX} ${startY} C ${startX + loopOffset} ${startY + 30}, ${startX + loopOffset} ${dropY}, ${(startX + endX) / 2} ${dropY} C ${endX - loopOffset} ${dropY}, ${endX - loopOffset} ${endY + 30}, ${endX} ${endY}`;
}
